import { useRef, useCallback } from 'react';

interface SwipeGestureOptions {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  threshold?: number;
}

const EDGE_GUARD = 24; // px from screen edges to avoid OS back/forward gestures

export const useHorizontalSwipe = ({
  onSwipeLeft,
  onSwipeRight,
  threshold = 30
}: SwipeGestureOptions) => {
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const hasTriggered = useRef<boolean>(false);
  const blockHorizontal = useRef<boolean>(false);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartX.current = touch.clientX;
    touchStartY.current = touch.clientY;
    hasTriggered.current = false;
    // Guard against OS edge-swipe (back/forward) gestures
    blockHorizontal.current =
      touch.clientX <= EDGE_GUARD || touch.clientX >= window.innerWidth - EDGE_GUARD;
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!e.touches[0] || hasTriggered.current || blockHorizontal.current) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartX.current;
    const deltaY = touch.clientY - touchStartY.current;

    // Horizontal swipe detection with immediate trigger
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > threshold) {
      e.preventDefault(); // Prevent vertical scrolling
      hasTriggered.current = true;

      if (deltaX > 0) {
        onSwipeRight?.(); // swipe right -> previous
      } else {
        onSwipeLeft?.(); // swipe left -> next
      }
    }
  }, [threshold, onSwipeLeft, onSwipeRight]);

  const handleTouchEnd = useCallback(() => {
    // Reset for next gesture
    hasTriggered.current = false;
    blockHorizontal.current = false;
  }, []);

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd
  };
};